import React, { useState, useEffect } from 'react';
import { useAdmin } from '../contexts/AdminContext';
import { adminAPI } from '../services/api';
import toast from 'react-hot-toast';
import QuickLogin from '../components/QuickLogin';
import AdminNavigation from '../components/AdminNavigation';
import SkinMappingInterface from '../components/SkinMappingInterface';
import ReverseSkinMappingInterface from '../components/ReverseSkinMappingInterface';
import { Search, Filter, RefreshCw, Download, Eye, Palette, Target, Package, TrendingUp, Map, CheckCircle, Clock, Database } from 'lucide-react';

const AdminSkinsPage = () => {
  const { isAuthenticated } = useAdmin();
  const [skins, setSkins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [pagination, setPagination] = useState({});
  const [stats, setStats] = useState({});
  const [filters, setFilters] = useState({});
  
  // Search and filter states
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedWeapon, setSelectedWeapon] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSkin, setSelectedSkin] = useState(null);
  
  // Mapping-related states
  const [activeTab, setActiveTab] = useState('skins'); // 'skins', 'mappings', or 'reverse-mappings'
  const [unmappedSkins, setUnmappedSkins] = useState([]);
  const [mappingPagination, setMappingPagination] = useState({});
  const [mappingPage, setMappingPage] = useState(1);
  const [mappingStats, setMappingStats] = useState({});
  const [currentMappingIndex, setCurrentMappingIndex] = useState(0);
  const [showMappedSkins, setShowMappedSkins] = useState(false);
  
  // Reverse mapping states
  const [unmappedDatabaseSkins, setUnmappedDatabaseSkins] = useState([]);
  const [reverseMappingPagination, setReverseMappingPagination] = useState({});
  const [reverseMappingPage, setReverseMappingPage] = useState(1);
  const [reverseMappingStats, setReverseMappingStats] = useState({});
  const [currentReverseMappingIndex, setCurrentReverseMappingIndex] = useState(0);

  const loadSkins = async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '25'
      });

      if (searchTerm) params.append('search', searchTerm);
      if (selectedWeapon) params.append('weapon', selectedWeapon);
      if (selectedCategory) params.append('category', selectedCategory);

      const response = await fetch(`http://localhost:8002/api/admin/skins?${params}`);
      const data = await response.json();

      if (data.success) {
        setSkins(data.data.skins);
        setPagination(data.data.pagination);
      } else {
        toast.error('Failed to load skins');
      }
    } catch (error) {
      console.error('Error loading skins:', error);
      toast.error('Failed to load skins');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await fetch('http://localhost:8002/api/admin/skins/stats');
      const data = await response.json();
      if (data.success) {
        setStats(data.data);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const loadFilters = async () => {
    try {
      const response = await fetch('http://localhost:8002/api/admin/skins/filters');
      const data = await response.json();
      if (data.success) {
        setFilters(data.data);
      }
    } catch (error) {
      console.error('Error loading filters:', error);
    }
  };

  const refreshSkins = async (clearExisting = false) => {
    setRefreshing(true);
    try {
      const response = await fetch('http://localhost:8002/api/admin/skins/refresh', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ clear: clearExisting })
      });

      const data = await response.json();
      
      if (data.success) {
        toast.success(`Skins refreshed successfully! Updated: ${data.data.stats.total_skins} skins`);
        await loadSkins(1);
        await loadStats();
      } else {
        toast.error(data.message || 'Failed to refresh skins');
      }
    } catch (error) {
      console.error('Error refreshing skins:', error);
      toast.error('Failed to refresh skins');
    } finally {
      setRefreshing(false);
    }
  };

  const viewSkinDetails = async (skinId) => {
    try {
      const response = await fetch(`http://localhost:8002/api/admin/skins/${skinId}`);
      const data = await response.json();
      if (data.success) {
        setSelectedSkin(data.data);
      }
    } catch (error) {
      console.error('Error loading skin details:', error);
      toast.error('Failed to load skin details');
    }
  };

  // Mapping functions
  const loadUnmappedSkins = async (page = 1, resetIndex = true) => {
    setLoading(true);
    try {
      const response = await adminAPI.getDemoSkinsForMapping(page, 25, showMappedSkins);
      if (response.data.success) {
        setUnmappedSkins(response.data.data.demo_skins);
        setMappingPagination(response.data.data.pagination);
        if (resetIndex) {
          setCurrentMappingIndex(0); // Only reset to first item when explicitly requested
        }
      } else {
        toast.error('Failed to load demo skins');
      }
    } catch (error) {
      console.error('Error loading demo skins:', error);
      toast.error('Failed to load demo skins');
    } finally {
      setLoading(false);
    }
  };

  const loadMappingStats = async () => {
    try {
      const response = await adminAPI.getMappingStats();
      if (response.data.success) {
        setMappingStats(response.data.data);
      }
    } catch (error) {
      console.error('Error loading mapping stats:', error);
    }
  };

  const handleMappingSaved = (mappingData) => {
    toast.success(`Mapped "${mappingData.demoSkin.demo_skin_name}" to "${mappingData.mappedSkin.name}"`);
    
    // Move to next unmapped skin
    if (currentMappingIndex < unmappedSkins.length - 1) {
      setCurrentMappingIndex(currentMappingIndex + 1);
    } else {
      // Load next page if available
      if (mappingPage < mappingPagination.last_page) {
        setMappingPage(mappingPage + 1);
      } else {
        toast.success('All skins on this page have been mapped!');
        loadUnmappedSkins(mappingPage, false); // Reload current page without resetting index
      }
    }
    
    // Refresh stats
    loadMappingStats();
  };

  const handleSkipMapping = () => {
    if (currentMappingIndex < unmappedSkins.length - 1) {
      setCurrentMappingIndex(currentMappingIndex + 1);
    } else {
      // Load next page if available
      if (mappingPage < mappingPagination.last_page) {
        setMappingPage(mappingPage + 1);
      } else {
        toast.info('No more skins to map on this page');
      }
    }
  };

  // Reverse mapping functions
  const loadUnmappedDatabaseSkins = async (page = 1) => {
    setLoading(true);
    try {
      const response = await adminAPI.getUnmappedDatabaseSkins(page, 25);
      if (response.data.success) {
        setUnmappedDatabaseSkins(response.data.data.unmapped_skins);
        setReverseMappingPagination(response.data.data.pagination);
        setCurrentReverseMappingIndex(0); // Reset to first item
      } else {
        toast.error('Failed to load unmapped database skins');
      }
    } catch (error) {
      console.error('Error loading unmapped database skins:', error);
      toast.error('Failed to load unmapped database skins');
    } finally {
      setLoading(false);
    }
  };

  const loadReverseMappingStats = async () => {
    try {
      const response = await adminAPI.getReverseMappingStats();
      if (response.data.success) {
        setReverseMappingStats(response.data.data);
      }
    } catch (error) {
      console.error('Error loading reverse mapping stats:', error);
    }
  };

  const handleReverseMappingSaved = (mappingData) => {
    toast.success(`Mapped "${mappingData.databaseSkin.name}" to demo skin "${mappingData.demoSkin.demo_skin_name}"`);
    
    // Move to next unmapped database skin
    if (currentReverseMappingIndex < unmappedDatabaseSkins.length - 1) {
      setCurrentReverseMappingIndex(currentReverseMappingIndex + 1);
    } else {
      // Load next page if available
      if (reverseMappingPage < reverseMappingPagination.last_page) {
        setReverseMappingPage(reverseMappingPage + 1);
      } else {
        toast.success('All database skins on this page have been mapped!');
        loadUnmappedDatabaseSkins(reverseMappingPage); // Reload current page
      }
    }
    
    // Refresh stats
    loadReverseMappingStats();
    loadMappingStats(); // Also refresh regular mapping stats since they share the same table
  };

  const handleSkipReverseMapping = () => {
    if (currentReverseMappingIndex < unmappedDatabaseSkins.length - 1) {
      setCurrentReverseMappingIndex(currentReverseMappingIndex + 1);
    } else {
      // Load next page if available
      if (reverseMappingPage < reverseMappingPagination.last_page) {
        setReverseMappingPage(reverseMappingPage + 1);
      } else {
        toast.info('No more database skins to map on this page');
      }
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      if (activeTab === 'skins') {
        loadSkins(1);
        loadStats();
        loadFilters();
      } else if (activeTab === 'mappings') {
        loadUnmappedSkins(1);
        loadMappingStats();
      } else if (activeTab === 'reverse-mappings') {
        loadUnmappedDatabaseSkins(1);
        loadReverseMappingStats();
      }
    }
  }, [isAuthenticated, activeTab]);

  // Separate useEffect for mapping filter changes
  useEffect(() => {
    if (isAuthenticated && activeTab === 'mappings') {
      loadUnmappedSkins(1);
      setMappingPage(1); // Reset to page 1 when filter changes
    }
  }, [showMappedSkins]);

  useEffect(() => {
    if (isAuthenticated) {
      if (activeTab === 'skins') {
        loadSkins(currentPage);
      }
    }
  }, [currentPage, searchTerm, selectedWeapon, selectedCategory]);

  useEffect(() => {
    if (isAuthenticated && activeTab === 'mappings') {
      loadUnmappedSkins(mappingPage);
    }
  }, [mappingPage, showMappedSkins]);

  useEffect(() => {
    if (isAuthenticated && activeTab === 'reverse-mappings') {
      loadUnmappedDatabaseSkins(reverseMappingPage);
    }
  }, [reverseMappingPage]);

  if (!isAuthenticated) {
    return <QuickLogin />;
  }

  const getRarityBadgeClass = (rarity) => {
    const rarityClasses = {
      'Consumer Grade': 'bg-gray-500 text-white',
      'Industrial Grade': 'bg-blue-500 text-white',
      'Mil-Spec Grade': 'bg-indigo-500 text-white',
      'Restricted': 'bg-purple-500 text-white',
      'Classified': 'bg-pink-500 text-white',
      'Covert': 'bg-red-500 text-white',
      'Extraordinary': 'bg-yellow-500 text-black',
      'Contraband': 'bg-orange-500 text-white'
    };
    
    return rarityClasses[rarity] || 'bg-gray-400 text-white';
  };

  return (
    <div className="min-h-screen bg-gray-900">
      <AdminNavigation />
      
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <Palette className="text-purple-400" />
              Skin Database Management
            </h1>
            <p className="mt-2 text-gray-300">
              Manage and view all CS2 skins imported from ByMykel's API
            </p>
          </div>

          {/* Tab Navigation */}
          <div className="mb-8">
            <div className="border-b border-gray-700">
              <nav className="-mb-px flex space-x-8">
                <button
                  onClick={() => setActiveTab('skins')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'skins'
                      ? 'border-purple-500 text-purple-400'
                      : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Package className="h-4 w-4" />
                    Skin Database
                  </div>
                </button>
                <button
                  onClick={() => setActiveTab('mappings')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'mappings'
                      ? 'border-purple-500 text-purple-400'
                      : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Map className="h-4 w-4" />
                    Demo → DB
                    {mappingStats.unmapped_count > 0 && (
                      <span className="bg-yellow-600 text-yellow-100 text-xs px-2 py-1 rounded-full">
                        {mappingStats.unmapped_count}
                      </span>
                    )}
                  </div>
                </button>
                <button
                  onClick={() => setActiveTab('reverse-mappings')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'reverse-mappings'
                      ? 'border-purple-500 text-purple-400'
                      : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Database className="h-4 w-4" />
                    DB → Demo
                    {reverseMappingStats.unmapped_database_skins > 0 && (
                      <span className="bg-blue-600 text-blue-100 text-xs px-2 py-1 rounded-full">
                        {reverseMappingStats.unmapped_database_skins}
                      </span>
                    )}
                  </div>
                </button>
              </nav>
            </div>
          </div>

          {/* Statistics Cards for Skins Tab */}
          {activeTab === 'skins' && stats.overview && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <div className="bg-gray-800 rounded-lg shadow p-6">
                <div className="flex items-center">
                  <Target className="h-8 w-8 text-blue-400" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-400">Total Skins</p>
                    <p className="text-2xl font-bold text-white">{stats.overview.total_skins}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-800 rounded-lg shadow p-6">
                <div className="flex items-center">
                  <Package className="h-8 w-8 text-green-400" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-400">Weapons</p>
                    <p className="text-2xl font-bold text-white">{stats.overview.total_weapons}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-800 rounded-lg shadow p-6">
                <div className="flex items-center">
                  <Palette className="h-8 w-8 text-purple-400" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-400">Patterns</p>
                    <p className="text-2xl font-bold text-white">{stats.overview.total_patterns}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-800 rounded-lg shadow p-6">
                <div className="flex items-center">
                  <TrendingUp className="h-8 w-8 text-orange-400" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-400">Categories</p>
                    <p className="text-2xl font-bold text-white">{stats.categories?.length || 0}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Statistics Cards for Mappings Tab */}
          {activeTab === 'mappings' && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <div className="bg-gray-800 rounded-lg shadow p-6">
                <div className="flex items-center">
                  <Map className="h-8 w-8 text-blue-400" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-400">Total Mappings</p>
                    <p className="text-2xl font-bold text-white">{mappingStats.total_mappings || 0}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-800 rounded-lg shadow p-6">
                <div className="flex items-center">
                  <Clock className="h-8 w-8 text-yellow-400" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-400">Unmapped</p>
                    <p className="text-2xl font-bold text-white">{mappingStats.unmapped_count || 0}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-800 rounded-lg shadow p-6">
                <div className="flex items-center">
                  <CheckCircle className="h-8 w-8 text-green-400" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-400">Progress</p>
                    <p className="text-2xl font-bold text-white">{mappingStats.mapping_percentage || 0}%</p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-800 rounded-lg shadow p-6">
                <div className="flex items-center">
                  <Target className="h-8 w-8 text-purple-400" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-400">Total Demo Skins</p>
                    <p className="text-2xl font-bold text-white">{mappingStats.total_unique_demo_skins || 0}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Statistics Cards for Reverse Mappings Tab */}
          {activeTab === 'reverse-mappings' && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <div className="bg-gray-800 rounded-lg shadow p-6">
                <div className="flex items-center">
                  <Database className="h-8 w-8 text-blue-400" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-400">Total DB Skins</p>
                    <p className="text-2xl font-bold text-white">{reverseMappingStats.total_database_skins || 0}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-800 rounded-lg shadow p-6">
                <div className="flex items-center">
                  <Clock className="h-8 w-8 text-yellow-400" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-400">Unmapped DB Skins</p>
                    <p className="text-2xl font-bold text-white">{reverseMappingStats.unmapped_database_skins || 0}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-800 rounded-lg shadow p-6">
                <div className="flex items-center">
                  <CheckCircle className="h-8 w-8 text-green-400" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-400">Mapped Progress</p>
                    <p className="text-2xl font-bold text-white">{reverseMappingStats.mapping_percentage || 0}%</p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-800 rounded-lg shadow p-6">
                <div className="flex items-center">
                  <Map className="h-8 w-8 text-purple-400" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-400">Total Mappings</p>
                    <p className="text-2xl font-bold text-white">{reverseMappingStats.total_mappings || 0}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Controls for Skins Tab */}
          {activeTab === 'skins' && (
            <div className="bg-gray-800 rounded-lg shadow p-6 mb-8">
              <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
              {/* Search and Filters */}
              <div className="flex flex-col sm:flex-row gap-4 flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <input
                    type="text"
                    placeholder="Search skins..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
                
                <select
                  value={selectedWeapon}
                  onChange={(e) => setSelectedWeapon(e.target.value)}
                  className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">All Weapons</option>
                  {filters.weapons?.map(weapon => (
                    <option key={weapon} value={weapon}>{weapon}</option>
                  ))}
                </select>
                
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">All Categories</option>
                  {filters.categories?.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2">
                <button
                  onClick={() => refreshSkins(false)}
                  disabled={refreshing}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                >
                  <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
                  Update
                </button>
                
                <button
                  onClick={() => refreshSkins(true)}
                  disabled={refreshing}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
                >
                  <Download className="h-4 w-4" />
                  Full Refresh
                </button>
              </div>
              </div>
            </div>
          )}

          {/* Skins Grid */}
          {activeTab === 'skins' && (
          <div className="bg-gray-800 rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-700">
              <h2 className="text-lg font-semibold text-white">
                Skins ({pagination.total || 0})
              </h2>
            </div>
            
            {loading ? (
              <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-700">
                  <thead className="bg-gray-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                        Skin
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                        Weapon
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                        Rarity
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                        Category
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                        Paint Index
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-gray-800 divide-y divide-gray-700">
                    {skins.map((skin) => (
                      <tr key={skin.id} className="hover:bg-gray-700">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-12 w-12">
                              {skin.image ? (
                                <img
                                  className="h-12 w-12 object-contain bg-gray-700 rounded"
                                  src={skin.image}
                                  alt={skin.name}
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextElementSibling.style.display = 'flex';
                                  }}
                                />
                              ) : null}
                              <div className="h-12 w-12 bg-gray-700 rounded flex items-center justify-center text-gray-400 text-xs" style={{ display: skin.image ? 'none' : 'flex' }}>
                                🔫
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-white">
                                {skin.name}
                              </div>
                              <div className="text-sm text-gray-400">
                                {skin.code}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                          {skin.weapon?.name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {skin.rarity?.name && (
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRarityBadgeClass(skin.rarity.name)}`}>
                              {skin.rarity.name}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                          {skin.category || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                          {skin.paint_index || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => viewSkinDetails(skin.id)}
                            className="text-purple-400 hover:text-purple-300 flex items-center gap-1"
                          >
                            <Eye className="h-4 w-4" />
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {pagination.last_page > 1 && (
              <div className="px-6 py-4 border-t border-gray-700 flex items-center justify-between">
                <div className="text-sm text-gray-300">
                  Showing {((pagination.current_page - 1) * pagination.per_page) + 1} to{' '}
                  {Math.min(pagination.current_page * pagination.per_page, pagination.total)} of{' '}
                  {pagination.total} results
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage(pagination.current_page - 1)}
                    disabled={pagination.current_page === 1}
                    className="px-3 py-2 border border-gray-600 rounded-md text-sm text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-700"
                  >
                    Previous
                  </button>
                  <span className="px-3 py-2 text-sm text-gray-300">
                    Page {pagination.current_page} of {pagination.last_page}
                  </span>
                  <button
                    onClick={() => setCurrentPage(pagination.current_page + 1)}
                    disabled={pagination.current_page === pagination.last_page}
                    className="px-3 py-2 border border-gray-600 rounded-md text-sm text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-700"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
          )}

          {/* Mapping Interface */}
          {activeTab === 'mappings' && (
            <div className="space-y-6">
              {/* Filter Toggle */}
              <div className="bg-gray-800 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="text-white">
                    <h3 className="text-lg font-semibold">Demo Skin Mapping</h3>
                    <p className="text-gray-400 text-sm">Map demo skins to database skins for better recognition</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-2 text-white cursor-pointer">
                      <input
                        type="checkbox"
                        checked={showMappedSkins}
                        onChange={(e) => {
                          setShowMappedSkins(e.target.checked);
                          setMappingPage(1); // Reset to first page when filter changes
                        }}
                        className="form-checkbox h-4 w-4 text-purple-600 bg-gray-700 border-gray-600 rounded focus:ring-purple-500"
                      />
                      <span className="text-sm">Show already mapped skins</span>
                    </label>
                  </div>
                </div>
              </div>

              {unmappedSkins.length > 0 ? (
                <div>
                  <div className="mb-4 flex items-center justify-between">
                    <div className="text-white">
                      <span className="text-lg font-semibold">
                        Mapping {currentMappingIndex + 1} of {unmappedSkins.length}
                      </span>
                      <span className="text-gray-400 ml-2">
                        (Page {mappingPage} of {mappingPagination.last_page || 1})
                      </span>
                      {unmappedSkins[currentMappingIndex]?.is_mapped && (
                        <span className="ml-3 px-2 py-1 bg-green-600 text-green-100 text-xs rounded-full">
                          ✓ Already Mapped
                        </span>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setCurrentMappingIndex(Math.max(0, currentMappingIndex - 1))}
                        disabled={currentMappingIndex === 0}
                        className="px-3 py-2 bg-gray-600 text-white rounded disabled:opacity-50"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => setCurrentMappingIndex(Math.min(unmappedSkins.length - 1, currentMappingIndex + 1))}
                        disabled={currentMappingIndex === unmappedSkins.length - 1}
                        className="px-3 py-2 bg-gray-600 text-white rounded disabled:opacity-50"
                      >
                        Next
                      </button>
                    </div>
                  </div>

                  <SkinMappingInterface
                    demoSkin={unmappedSkins[currentMappingIndex]}
                    onMappingSaved={handleMappingSaved}
                    onSkip={handleSkipMapping}
                  />

                  {/* Pagination for mapping pages */}
                  {mappingPagination.last_page > 1 && (
                    <div className="mt-6 flex justify-center">
                      <div className="flex gap-2">
                        <button
                          onClick={() => setMappingPage(mappingPage - 1)}
                          disabled={mappingPage === 1}
                          className="px-3 py-2 border border-gray-600 rounded-md text-sm text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-700"
                        >
                          Previous Page
                        </button>
                        <span className="px-3 py-2 text-sm text-gray-300">
                          Page {mappingPage} of {mappingPagination.last_page}
                        </span>
                        <button
                          onClick={() => setMappingPage(mappingPage + 1)}
                          disabled={mappingPage === mappingPagination.last_page}
                          className="px-3 py-2 border border-gray-600 rounded-md text-sm text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-700"
                        >
                          Next Page
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : loading ? (
                <div className="flex justify-center items-center h-64">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <CheckCircle className="h-16 w-16 text-green-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">All Skins Mapped!</h3>
                  <p className="text-gray-400">There are no unmapped demo skins to process.</p>
                </div>
              )}
            </div>
          )}

          {/* Reverse Mapping Interface */}
          {activeTab === 'reverse-mappings' && (
            <div className="space-y-6">
              {unmappedDatabaseSkins.length > 0 ? (
                <div>
                  <div className="mb-4 flex items-center justify-between">
                    <div className="text-white">
                      <span className="text-lg font-semibold">
                        Mapping {currentReverseMappingIndex + 1} of {unmappedDatabaseSkins.length}
                      </span>
                      <span className="text-gray-400 ml-2">
                        (Page {reverseMappingPage} of {reverseMappingPagination.last_page || 1})
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setCurrentReverseMappingIndex(Math.max(0, currentReverseMappingIndex - 1))}
                        disabled={currentReverseMappingIndex === 0}
                        className="px-3 py-2 bg-gray-600 text-white rounded disabled:opacity-50"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => setCurrentReverseMappingIndex(Math.min(unmappedDatabaseSkins.length - 1, currentReverseMappingIndex + 1))}
                        disabled={currentReverseMappingIndex === unmappedDatabaseSkins.length - 1}
                        className="px-3 py-2 bg-gray-600 text-white rounded disabled:opacity-50"
                      >
                        Next
                      </button>
                    </div>
                  </div>

                  <ReverseSkinMappingInterface
                    databaseSkin={unmappedDatabaseSkins[currentReverseMappingIndex]}
                    onMappingSaved={handleReverseMappingSaved}
                    onSkip={handleSkipReverseMapping}
                  />

                  {/* Pagination for reverse mapping pages */}
                  {reverseMappingPagination.last_page > 1 && (
                    <div className="mt-6 flex justify-center">
                      <div className="flex gap-2">
                        <button
                          onClick={() => setReverseMappingPage(reverseMappingPage - 1)}
                          disabled={reverseMappingPage === 1}
                          className="px-3 py-2 border border-gray-600 rounded-md text-sm text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-700"
                        >
                          Previous Page
                        </button>
                        <span className="px-3 py-2 text-sm text-gray-300">
                          Page {reverseMappingPage} of {reverseMappingPagination.last_page}
                        </span>
                        <button
                          onClick={() => setReverseMappingPage(reverseMappingPage + 1)}
                          disabled={reverseMappingPage === reverseMappingPagination.last_page}
                          className="px-3 py-2 border border-gray-600 rounded-md text-sm text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-700"
                        >
                          Next Page
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : loading ? (
                <div className="flex justify-center items-center h-64">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <CheckCircle className="h-16 w-16 text-green-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">All Database Skins Mapped!</h3>
                  <p className="text-gray-400">Every skin in the database has been mapped to a demo skin.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Skin Details Modal */}
      {selectedSkin && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-gray-800 rounded-lg max-w-2xl w-full max-h-96 overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-lg font-semibold text-white">{selectedSkin.name}</h3>
                <button
                  onClick={() => setSelectedSkin(null)}
                  className="text-gray-400 hover:text-gray-300"
                >
                  ✕
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  {selectedSkin.image && (
                    <img
                      src={selectedSkin.image}
                      alt={selectedSkin.name}
                      className="w-full h-48 object-contain bg-gray-700 rounded"
                    />
                  )}
                </div>
                
                <div className="space-y-3 text-gray-300">
                  <div>
                    <span className="font-medium text-white">Code:</span> {selectedSkin.code}
                  </div>
                  <div>
                    <span className="font-medium text-white">Weapon:</span> {selectedSkin.weapon?.name}
                  </div>
                  <div>
                    <span className="font-medium text-white">Pattern:</span> {selectedSkin.pattern?.name || '-'}
                  </div>
                  <div>
                    <span className="font-medium text-white">Rarity:</span> {selectedSkin.rarity?.name || '-'}
                  </div>
                  <div>
                    <span className="font-medium text-white">Paint Index:</span> {selectedSkin.paint_index || '-'}
                  </div>
                  <div>
                    <span className="font-medium text-white">Float Range:</span> {selectedSkin.float_range?.min || 0} - {selectedSkin.float_range?.max || 1}
                  </div>
                  <div>
                    <span className="font-medium text-white">StatTrak:</span> {selectedSkin.stattrak ? 'Yes' : 'No'}
                  </div>
                  <div>
                    <span className="font-medium text-white">Souvenir:</span> {selectedSkin.souvenir ? 'Yes' : 'No'}
                  </div>
                </div>
              </div>
              
              {selectedSkin.description && (
                <div className="mt-6">
                  <span className="font-medium text-white">Description:</span>
                  <p className="mt-2 text-gray-300 text-sm">{selectedSkin.description}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSkinsPage;
